/**
 * Fehlerklasse des Konnektor-Vertrags ([A] 3.1, Plan Teil A Anhang B, R-32).
 * `detail` stammt aus einem geschlossenen Katalog statischer Codes; Freitext (Antwortkörper,
 * Namen, Tokens) kann nicht in Meldung oder Protokoll gelangen.
 */
import { z } from 'zod';

export const CONNECTOR_ERROR_CLASSES = [
  'auth',
  'rate_limited',
  'transient',
  'saturated',
  'protocol',
  'permanent',
  'aborted',
  'not_ready',
] as const;

export const ConnectorErrorClassSchema = z.enum(CONNECTOR_ERROR_CLASSES);
export type ConnectorErrorClass = z.infer<typeof ConnectorErrorClassSchema>;

/** Geschlossener Katalog; neue Codes nur per Vertragsänderung (`C-<n>`, [V] 6.1 Nr. 4). */
export const CONNECTOR_ERROR_DETAILS = [
  'stale_token',
  'unbound_token',
  'account_mismatch',
  'consent_revoked',
  'not_ready',
  'http_400',
  'http_401',
  'http_403',
  'http_404_no_data',
  'http_404_other',
  'http_409',
  'http_410',
  'http_422',
  'http_429',
  'http_5xx',
  'timeout',
  'connect_failed',
  'aborted_signal',
  'invalid_json',
  'unknown_shape',
  'missing_field',
  'page_limit',
  'cursor_version',
  'cursor_stream',
  'saturated',
  'unspecified',
] as const;

export const ConnectorErrorDetailSchema = z.enum(CONNECTOR_ERROR_DETAILS);
export type ConnectorErrorDetail = z.infer<typeof ConnectorErrorDetailSchema>;

const DETAIL_SET: ReadonlySet<unknown> = new Set(CONNECTOR_ERROR_DETAILS);
const CLASS_SET: ReadonlySet<unknown> = new Set(CONNECTOR_ERROR_CLASSES);

/** Prüft zur Laufzeit, ob ein Wert ein Katalogcode ist (Schutz vor `as`-Casts). */
export function isConnectorErrorDetail(value: unknown): value is ConnectorErrorDetail {
  return DETAIL_SET.has(value);
}

export class ConnectorError extends Error {
  override readonly name = 'ConnectorError';
  readonly errorClass: ConnectorErrorClass;
  readonly detail: ConnectorErrorDetail;
  /** Nur bei `rate_limited`. */
  readonly retryAt?: Date;
  readonly httpStatus?: number;

  constructor(
    errorClass: ConnectorErrorClass,
    detail: ConnectorErrorDetail,
    extra?: { retryAt?: Date; httpStatus?: number },
  ) {
    if (!CLASS_SET.has(errorClass)) {
      throw new RangeError('ConnectorError: unbekannte Fehlerklasse');
    }
    const safeDetail: ConnectorErrorDetail = isConnectorErrorDetail(detail)
      ? detail
      : 'unspecified';
    super(`${errorClass}: ${safeDetail}`);
    this.errorClass = errorClass;
    this.detail = safeDetail;
    if (extra?.retryAt !== undefined) {
      if (errorClass !== 'rate_limited') {
        throw new RangeError('ConnectorError: retryAt ist nur bei rate_limited zulässig');
      }
      this.retryAt = extra.retryAt;
    }
    if (extra?.httpStatus !== undefined) {
      this.httpStatus = extra.httpStatus;
    }
  }
}
